import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    LAST_ACTIVE_HEARTBEAT_INTERVAL_MS,
    recordUserLastActive,
} from '../services/userLastActiveService';

/**
 * Updates `user_last_active/{uid}` while the wholesale app is in use: immediate write on session start,
 * then throttled ticks (~10s default) while the tab is visible. Tab focus / visibility / pagehide flush with force.
 */
export default function LastActiveHeartbeat() {
    const { user } = useAuth();
    const uid = user?.uid;
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!uid) return;

        const tick = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            recordUserLastActive(uid);
        };

        // Capture “online” as soon as the account is active (London Smoke & Cure — cart window / admin views).
        recordUserLastActive(uid, { force: true });

        intervalRef.current = window.setInterval(tick, LAST_ACTIVE_HEARTBEAT_INTERVAL_MS);

        const onVis = () => {
            if (document.visibilityState === 'visible') {
                recordUserLastActive(uid, { force: true });
            }
        };
        const onFocus = () => {
            recordUserLastActive(uid, { force: true });
        };
        const onPageHide = () => recordUserLastActive(uid, { force: true });

        document.addEventListener('visibilitychange', onVis);
        window.addEventListener('focus', onFocus);
        window.addEventListener('pagehide', onPageHide);

        return () => {
            if (intervalRef.current) window.clearInterval(intervalRef.current);
            document.removeEventListener('visibilitychange', onVis);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('pagehide', onPageHide);
        };
    }, [uid]);

    return null;
}
